// OB-21 §3.6 — which reservations are holding one (cap, month). Fetched on open, never with the
// page. On a shared line the sibling side is ALREADY folded server-side into `unattributed` — this
// component never receives, and must never invent, a sibling's identity.

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogTitle, DialogContent, Table, TableBody, TableCell, TableHead, TableRow, CircularProgress, Button } from '@mui/material';
import { formatMoneyAmount, hasUnattributedRemainder } from './creditLineDisplay';

const CreditLineDrilldown = ({ open, onClose, month, loading, drilldown, onPageChange }) => {
	const { t } = useTranslation();

	const page = (drilldown && drilldown.page) || 1;
	const total = (drilldown && drilldown.total) || 0;
	const limit = (drilldown && drilldown.limit) || 0;
	// Older payloads carry no paging keys at all; without `limit` there is nothing to page through,
	// so the controls stay hidden rather than rendering a one-page pager.
	const has_pages = Boolean(onPageChange) && limit > 0 && total > limit;

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle>{t('CreditLinePage.DrilldownTitle', { month })}</DialogTitle>
			<DialogContent>
				{loading && <CircularProgress size={24} />}

				{!loading && drilldown && (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>{t('CreditLinePage.DrilldownReservation')}</TableCell>
								<TableCell>{t('CreditLinePage.DrilldownHotel')}</TableCell>
								<TableCell>{t('CreditLinePage.DrilldownDates')}</TableCell>
								<TableCell>{t('CreditLinePage.DrilldownBooker')}</TableCell>
								<TableCell>{t('CreditLinePage.DrilldownStatus')}</TableCell>
								<TableCell align="right">{t('CreditLinePage.DrilldownAmount')}</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{drilldown.reservations.map((reservation) => (
								<TableRow key={reservation.reservation_id}>
									<TableCell>{reservation.short_id || '—'}</TableCell>
									<TableCell>{reservation.hotel_name || '—'}</TableCell>
									<TableCell>
										{reservation.check_in ? new Date(reservation.check_in).toLocaleDateString() : '—'}
										{' – '}
										{reservation.check_out ? new Date(reservation.check_out).toLocaleDateString() : '—'}
									</TableCell>
									<TableCell>{reservation.booker || '—'}</TableCell>
									<TableCell>
										{reservation.is_request
											? t('CreditLinePage.DrilldownRequest')
											: reservation.status || '—'}
									</TableCell>
									<TableCell align="right">
										{formatMoneyAmount(reservation.amount)} {reservation.currency}
									</TableCell>
								</TableRow>
							))}

							{drilldown.reservations.length === 0 && !hasUnattributedRemainder(drilldown) && (
								<TableRow>
									<TableCell colSpan={6}>{t('CreditLinePage.DrilldownEmpty')}</TableCell>
								</TableRow>
							)}

							{hasUnattributedRemainder(drilldown) && (
								<TableRow>
									<TableCell colSpan={5} style={{ fontStyle: 'italic' }}>
										{t('CreditLinePage.DrilldownUnattributed')}
									</TableCell>
									<TableCell align="right" style={{ fontStyle: 'italic' }}>
										{formatMoneyAmount(drilldown.unattributed.amount)} {drilldown.unattributed.currency}
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				)}

				{!loading && has_pages && (
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'flex-end',
							gap: '12px',
							padding: '12px 0',
						}}
					>
						<span>{t('CreditLinePage.DrilldownPageOf', { page, pages: Math.ceil(total / limit) })}</span>
						<Button size="small" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
							{t('CreditLinePage.DrilldownPrevious')}
						</Button>
						<Button size="small" disabled={!drilldown.has_more} onClick={() => onPageChange(page + 1)}>
							{t('CreditLinePage.DrilldownNext')}
						</Button>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
};

export default CreditLineDrilldown;
